import { useEffect } from 'react';
import type { BookingTypeLabel } from '../types';
import { trackEvent } from '../utils/track';

interface ConfirmationScreenProps {
  bookingType: BookingTypeLabel;
  experienceTitle: string;
  startsAt: string;
  partySize: number;
  customerName: string;
  /** Conversation.Id devolvido pelo POST /booking/request — usado como código de rastreio na tela. */
  trackingId?: number | null;
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ConfirmationScreen({
  bookingType,
  experienceTitle,
  startsAt,
  partySize,
  customerName,
  trackingId,
}: ConfirmationScreenProps) {
  const isInstant = bookingType === 'Confirmação Imediata';
  const firstName = customerName.split(' ')[0] || '';

  useEffect(() => {
    trackEvent('booking_confirmation_view', {
      bookingType,
      partySize,
      eventTime: startsAt,
      conversationId: trackingId ?? undefined,
      surface: 'onrequest_inline',
    });
  }, [bookingType, partySize, startsAt, trackingId]);

  return (
    <main className={`confirm-screen confirm-screen--${isInstant ? 'instant' : 'request'}`}>
      <div className="confirm-screen__icon-wrap">
        {isInstant ? (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        ) : (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 7v5l3 3" />
          </svg>
        )}
      </div>

      <h1 className="confirm-screen__title">
        {isInstant ? 'Reserva confirmada!' : 'Solicitação enviada!'}
      </h1>

      <p className="confirm-screen__lead">
        {isInstant
          ? `${firstName}, sua vaga está garantida.`
          : `${firstName}, vamos confirmar com o operador e te avisar pelo WhatsApp.`}
      </p>

      <div className="confirm-screen__card">
        <div className="confirm-screen__row">
          <span>Experiência</span><strong>{experienceTitle}</strong>
        </div>
        <div className="confirm-screen__row">
          <span>Quando</span><strong>{formatDateTime(startsAt)}</strong>
        </div>
        <div className="confirm-screen__row">
          <span>Pessoas</span><strong>{partySize}</strong>
        </div>
        {!isInstant && trackingId != null && (
          <div className="confirm-screen__row">
            <span>Código de rastreio</span><strong>#{trackingId}</strong>
          </div>
        )}
      </div>

      {!isInstant && (
        <div className="confirm-screen__note">
          <strong>Você ainda não pagou.</strong> Após a confirmação do operador, enviamos o link de pagamento pelo WhatsApp.
        </div>
      )}

      <div className="confirm-screen__cta-row">
        <a href="/" className="btn btn--brand">Voltar para a home</a>
      </div>
    </main>
  );
}
